import { api, throwApiError } from "../../../api/api";
import type { DpContractType, DpEmployeeStatus, DpPaginated } from "./dpTypes";
import type { DpDocumentCategory } from "./documentsApi";

export type DpProcessKind = "ADMISSAO" | "DESLIGAMENTO";
export type DpProcessStatus = "EM_ANDAMENTO" | "CONCLUIDO" | "CANCELADO";
export type DpStepStatus = "PENDENTE" | "CONCLUIDO" | "NAO_APLICAVEL";
export type DpStepResponsible = "DP" | "GESTOR" | "COLABORADOR" | "TI" | "FINANCEIRO" | "OUTRO";
export type DpSystemAction = "CRIAR_CREDENCIAL" | "DESATIVAR_CREDENCIAL";
export type DpTerminationType =
  | "SEM_JUSTA_CAUSA"
  | "COM_JUSTA_CAUSA"
  | "PEDIDO_DEMISSAO"
  | "ACORDO_MUTUO"
  | "TERMINO_CONTRATO"
  | "FALECIMENTO"
  | "RESCISAO_INDIRETA"
  | "ANTECIPADA_EMPREGADOR"
  | "ANTECIPADA_EMPREGADO";
export type DpNoticeType = "TRABALHADO" | "INDENIZADO" | "DISPENSADO" | "NAO_APLICAVEL";

export const PROCESS_KIND_LABELS: Record<DpProcessKind, string> = {
  ADMISSAO: "Admissão",
  DESLIGAMENTO: "Desligamento",
};

export const PROCESS_STATUS_LABELS: Record<DpProcessStatus, string> = {
  EM_ANDAMENTO: "Em andamento",
  CONCLUIDO: "Concluído",
  CANCELADO: "Cancelado",
};

export const PROCESS_STATUS_BADGE: Record<DpProcessStatus, string> = {
  EM_ANDAMENTO: "dpBadge--blue",
  CONCLUIDO: "dpBadge--green",
  CANCELADO: "dpBadge--gray",
};

export const STEP_STATUS_LABELS: Record<DpStepStatus, string> = {
  PENDENTE: "Pendente",
  CONCLUIDO: "Concluída",
  NAO_APLICAVEL: "Não se aplica",
};

export const STEP_RESPONSIBLE_LABELS: Record<DpStepResponsible, string> = {
  DP: "DP",
  GESTOR: "Gestor",
  COLABORADOR: "Colaborador",
  TI: "TI",
  FINANCEIRO: "Financeiro",
  OUTRO: "Outro",
};

export const SYSTEM_ACTION_LABELS: Record<DpSystemAction, string> = {
  CRIAR_CREDENCIAL: "Criar acesso ao app",
  DESATIVAR_CREDENCIAL: "Desativar acesso ao app",
};

/** Qual ação de sistema cada tipo de processo aceita — espelha o backend. */
export const SYSTEM_ACTION_BY_KIND: Record<DpProcessKind, DpSystemAction> = {
  ADMISSAO: "CRIAR_CREDENCIAL",
  DESLIGAMENTO: "DESATIVAR_CREDENCIAL",
};

export const TERMINATION_TYPE_LABELS: Record<DpTerminationType, string> = {
  SEM_JUSTA_CAUSA: "Dispensa sem justa causa",
  COM_JUSTA_CAUSA: "Dispensa com justa causa",
  PEDIDO_DEMISSAO: "Pedido de demissão",
  ACORDO_MUTUO: "Acordo mútuo (art. 484-A)",
  TERMINO_CONTRATO: "Término do contrato (fim do prazo ou da experiência)",
  ANTECIPADA_EMPREGADOR: "Rescisão antecipada pela empresa (art. 479)",
  ANTECIPADA_EMPREGADO: "Rescisão antecipada pelo colaborador (art. 480)",
  FALECIMENTO: "Falecimento",
  RESCISAO_INDIRETA: "Rescisão indireta",
};

export const NOTICE_TYPE_LABELS: Record<DpNoticeType, string> = {
  TRABALHADO: "Trabalhado",
  INDENIZADO: "Indenizado",
  DISPENSADO: "Dispensado",
  NAO_APLICAVEL: "Não se aplica",
};

/** Desligamentos sem aviso prévio — espelha o backend (só para guiar o formulário). */
export const TERMINATIONS_WITHOUT_NOTICE: DpTerminationType[] = [
  "COM_JUSTA_CAUSA",
  "FALECIMENTO",
  "TERMINO_CONTRATO",
  "ANTECIPADA_EMPREGADOR",
  "ANTECIPADA_EMPREGADO",
];

// ── Modelos ─────────────────────────────────────────────────────────────────

export type DpTemplateStep = {
  id?: string;
  order?: number;
  title: string;
  description: string | null;
  responsible: DpStepResponsible;
  documentTypeId: string | null;
  documentType?: { id: string; code: string; name: string; category: DpDocumentCategory } | null;
  dueOffsetDays: number | null;
  systemAction: DpSystemAction | null;
};

export type DpProcessTemplate = {
  id: string;
  kind: DpProcessKind;
  name: string;
  isDefault: boolean;
  isActive: boolean;
  steps: DpTemplateStep[];
};

export type ProcessTemplatePayload = {
  kind?: DpProcessKind;
  name: string;
  isDefault: boolean;
  isActive: boolean;
  steps: Array<Omit<DpTemplateStep, "id" | "order" | "documentType">>;
};

// ── Processos ───────────────────────────────────────────────────────────────

export type DpProcessListItem = {
  id: string;
  kind: DpProcessKind;
  status: DpProcessStatus;
  baseDate: string;
  employee: { id: string; fullName: string };
  template: { id: string; name: string } | null;
  createdAt: string;
  totalSteps: number;
  pendingSteps: number;
  overdueSteps: number;
};

export type DpProcessStep = {
  id: string;
  order: number;
  title: string;
  description: string | null;
  responsible: DpStepResponsible;
  assignee: { id: string; nomeCompleto: string } | null;
  documentTypeId: string | null;
  documentType: { id: string; code: string; name: string; category: DpDocumentCategory } | null;
  dueDate: string | null;
  systemAction: DpSystemAction | null;
  systemActionRanAt: string | null;
  status: DpStepStatus;
  document: {
    id: string;
    title: string | null;
    issuedAt: string;
    expiresAt: string | null;
    assetId: string | null;
    fileName: string | null;
    deletedAt: string | null;
  } | null;
  completedAt: string | null;
  completedBy: { id: string; nomeCompleto: string } | null;
  note: string | null;
  isOverdue: boolean;
};

export type DpProcessDetail = {
  id: string;
  kind: DpProcessKind;
  status: DpProcessStatus;
  baseDate: string;
  employeeId: string;
  employee: { id: string; fullName: string; cpf: string | null; status: DpEmployeeStatus };
  contract: {
    id: string;
    contractType: DpContractType;
    admissionDate: string;
    registrationNumber: string | null;
    employer: { id: string; legalName: string; tradeName: string | null };
    termination: { terminationDate: string } | null;
  };
  template: { id: string; name: string } | null;
  createdBy: { id: string; nomeCompleto: string };
  terminationType: DpTerminationType | null;
  noticeType: DpNoticeType | null;
  noticeStartDate: string | null;
  terminationDate: string | null;
  terminationReason: string | null;
  completedAt: string | null;
  canceledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  steps: DpProcessStep[];
};

export type TerminationPayload = {
  contractId: string;
  templateId?: string;
  terminationType: DpTerminationType;
  noticeType: DpNoticeType;
  noticeStartDate?: string | null;
  lastWorkingDay: string;
  terminationDate?: string | null;
  reason?: string | null;
};

export type TerminationPreview = {
  terminationDate: string;
  projectedTerminationDate: string;
  noticeDays: number | null;
  /** Contrato a termo encerrado antes do prazo: dias que faltavam. */
  remainingTermDays: number | null;
  warnings: string[];
};

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

const request = async <T>(run: () => Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await run();
    return data;
  } catch (error) {
    throwApiError(error, fallback);
    throw error;
  }
};

export const listProcessTemplatesApi = (token: string, kind?: DpProcessKind, includeInactive = false) =>
  request<DpProcessTemplate[]>(
    () =>
      api.get("/dp/process-templates", {
        ...auth(token),
        params: { ...(kind ? { kind } : {}), ...(includeInactive ? { includeInactive: "true" } : {}) },
      }),
    "Erro ao listar os checklists."
  );

export const createProcessTemplateApi = (token: string, payload: ProcessTemplatePayload) =>
  request<DpProcessTemplate>(() => api.post("/dp/process-templates", payload, auth(token)), "Erro ao criar o checklist.");

export const updateProcessTemplateApi = (token: string, id: string, payload: Partial<ProcessTemplatePayload>) =>
  request<DpProcessTemplate>(
    () => api.patch(`/dp/process-templates/${id}`, payload, auth(token)),
    "Erro ao salvar o checklist."
  );

export const seedProcessDefaultsApi = (token: string) =>
  request<{ created: string[] }>(
    () => api.post("/dp/process-templates/seed-defaults", {}, auth(token)),
    "Erro ao criar os padrões."
  );

export const listProcessesApi = (
  token: string,
  params: {
    page?: number;
    limit?: number;
    kind?: DpProcessKind;
    status?: DpProcessStatus;
    employeeId?: string;
    overdueOnly?: boolean;
  }
) => {
  const { overdueOnly, ...rest } = params;
  return request<DpPaginated<DpProcessListItem>>(
    () => api.get("/dp/processes", { ...auth(token), params: { ...rest, ...(overdueOnly ? { overdueOnly: "true" } : {}) } }),
    "Erro ao listar os processos."
  );
};

export const getProcessesSummaryApi = (token: string) =>
  request<{ open: number; overdue: number }>(
    () => api.get("/dp/processes/summary", auth(token)),
    "Erro ao consultar os processos."
  );

export const getProcessApi = (token: string, id: string) =>
  request<DpProcessDetail>(() => api.get(`/dp/processes/${id}`, auth(token)), "Erro ao carregar o processo.");

export const openAdmissionApi = (token: string, employeeId: string, payload: { contractId: string; templateId?: string }) =>
  request<DpProcessDetail>(
    () => api.post(`/dp/employees/${employeeId}/processes/admission`, payload, auth(token)),
    "Erro ao iniciar a admissão."
  );

export const previewTerminationApi = (token: string, employeeId: string, payload: TerminationPayload) =>
  request<TerminationPreview>(
    () => api.post(`/dp/employees/${employeeId}/processes/termination/preview`, payload, auth(token)),
    "Erro ao projetar o desligamento."
  );

export const openTerminationApi = (token: string, employeeId: string, payload: TerminationPayload) =>
  request<DpProcessDetail & { warnings: string[] }>(
    () => api.post(`/dp/employees/${employeeId}/processes/termination`, payload, auth(token)),
    "Erro ao iniciar o desligamento."
  );

export const completeStepApi = (
  token: string,
  processId: string,
  stepId: string,
  payload: { note?: string | null; documentId?: string | null }
) =>
  request<DpProcessDetail>(
    () => api.post(`/dp/processes/${processId}/steps/${stepId}/complete`, payload, auth(token)),
    "Erro ao concluir a etapa."
  );

export const runStepActionApi = (token: string, processId: string, stepId: string) =>
  request<DpProcessDetail & { provisionalPassword?: string }>(
    () => api.post(`/dp/processes/${processId}/steps/${stepId}/run-action`, {}, auth(token)),
    "Erro ao executar a ação da etapa."
  );

export const skipStepApi = (token: string, processId: string, stepId: string, note: string) =>
  request<DpProcessDetail>(
    () => api.post(`/dp/processes/${processId}/steps/${stepId}/skip`, { note }, auth(token)),
    "Erro ao marcar a etapa."
  );

export const reopenStepApi = (token: string, processId: string, stepId: string) =>
  request<DpProcessDetail>(
    () => api.post(`/dp/processes/${processId}/steps/${stepId}/reopen`, {}, auth(token)),
    "Erro ao reabrir a etapa."
  );

export const updateStepApi = (
  token: string,
  processId: string,
  stepId: string,
  payload: { assigneeId?: string | null; dueDate?: string | null }
) =>
  request<DpProcessDetail>(
    () => api.patch(`/dp/processes/${processId}/steps/${stepId}`, payload, auth(token)),
    "Erro ao salvar a etapa."
  );

export const completeProcessApi = (token: string, processId: string) =>
  // warnings: o desligamento recalcula o demonstrativo de rescisão; falha vira aviso.
  request<DpProcessDetail & { warnings?: string[] }>(
    () => api.post(`/dp/processes/${processId}/complete`, {}, auth(token)),
    "Erro ao concluir o processo."
  );

export const cancelProcessApi = (token: string, processId: string, reason: string) =>
  // financeiroWarnings: título do demonstrativo de rescisão com baixa no Financeiro (ADR 0029).
  request<DpProcessDetail & { credentialDisabledByProcess: boolean; financeiroWarnings?: string[] }>(
    () => api.post(`/dp/processes/${processId}/cancel`, { reason }, auth(token)),
    "Erro ao cancelar o processo."
  );
